import React, { useMemo } from 'react';
import { CheckCircle2, AlertTriangle, XCircle, Search } from 'lucide-react';

/**
 * Yoast-style SEO scoring panel for a product.
 *
 * Props:
 *   - product: { name, slug, description, image, ... }
 *   - focusKeyword: string (optional) — what we score the content against
 *   - onFocusKeywordChange: fn(string)
 */

const stripTags = (s) => (s || '').replace(/<[^>]*>/g, ' ');
const wordCount = (s) => stripTags(s).split(/\s+/).filter(Boolean).length;
const contains = (haystack, needle) => {
  if (!needle) return false;
  return (haystack || '').toLowerCase().includes(needle.toLowerCase());
};

const Check = ({ status, label, hint }) => {
  const config = {
    good: { Icon: CheckCircle2, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
    ok: { Icon: AlertTriangle, color: 'text-amber-400', bg: 'bg-amber-500/10' },
    bad: { Icon: XCircle, color: 'text-rose-400', bg: 'bg-rose-500/10' },
  }[status];
  const { Icon, color, bg } = config;
  return (
    <div className={`flex items-start gap-2 p-2 rounded ${bg}`}>
      <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${color}`} />
      <div className="text-xs">
        <p className="text-zinc-200">{label}</p>
        {hint && <p className="text-zinc-500 mt-0.5">{hint}</p>}
      </div>
    </div>
  );
};

const SeoYoast = ({ product, focusKeyword, onFocusKeywordChange }) => {
  const checks = useMemo(() => {
    const fk = (focusKeyword || '').trim();
    const name = product?.name || '';
    const slug = product?.slug || '';
    const desc = product?.description || '';
    const image = product?.image || '';
    const words = wordCount(desc);

    const results = [];

    // 1. Focus keyword set
    results.push({
      key: 'fk_set',
      status: fk ? 'good' : 'bad',
      label: fk ? `Focus keyword set: "${fk}"` : 'No focus keyword set',
      hint: fk ? null : 'Pick the main term shoppers search for, e.g. "banana ice 20k vape".',
      weight: 0,
    });
    if (!fk) {
      return results;
    }

    // 2. Keyword in title
    results.push({
      key: 'kw_in_title',
      status: contains(name, fk) ? 'good' : 'bad',
      label: contains(name, fk) ? 'Focus keyword appears in the product name' : 'Focus keyword missing from product name',
      hint: 'Including the keyword in the name helps search ranking.',
      weight: 20,
    });

    // 3. Keyword in slug
    const slugKw = fk.toLowerCase().replace(/\s+/g, '-');
    results.push({
      key: 'kw_in_slug',
      status: slug.includes(slugKw) || contains(slug, fk) ? 'good' : 'ok',
      label: slug.includes(slugKw) ? 'Focus keyword appears in slug' : 'Focus keyword missing in slug',
      hint: 'Slugs with the keyword get a small ranking boost.',
      weight: 10,
    });

    // 4. Slug length
    const slugLen = (slug || '').length;
    results.push({
      key: 'slug_len',
      status: slugLen >= 3 && slugLen <= 75 ? 'good' : 'ok',
      label: `Slug length: ${slugLen} chars`,
      hint: slugLen > 75 ? 'Keep under 75 chars for clean URLs.' : 'Aim for 20-60 chars.',
      weight: 5,
    });

    // 5. Title length
    const titleLen = name.length;
    let titleStatus = 'bad';
    if (titleLen >= 25 && titleLen <= 70) titleStatus = 'good';
    else if (titleLen >= 15) titleStatus = 'ok';
    results.push({
      key: 'title_len',
      status: titleStatus,
      label: `Product name length: ${titleLen} chars`,
      hint: 'Optimal product name length is 25-70 characters for Google snippets.',
      weight: 10,
    });

    // 6. Description length
    let descStatus = 'bad';
    if (words >= 60) descStatus = 'good';
    else if (words >= 25) descStatus = 'ok';
    results.push({
      key: 'desc_len',
      status: descStatus,
      label: `Description: ${words} words`,
      hint: 'Aim for 60+ words. Helps Google understand the product.',
      weight: 15,
    });

    // 7. Keyword in description
    const descCount = (desc.toLowerCase().match(new RegExp(fk.toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g')) || []).length;
    let descKwStatus = 'bad';
    if (descCount >= 2 && descCount <= 6) descKwStatus = 'good';
    else if (descCount === 1 || descCount > 6) descKwStatus = 'ok';
    results.push({
      key: 'kw_in_desc',
      status: descKwStatus,
      label: `Focus keyword in description: ${descCount}×`,
      hint: descCount > 6 ? 'Avoid keyword stuffing (keep under 6×).' : 'Aim for 2-4 mentions across the text.',
      weight: 15,
    });

    // 8. Image set
    results.push({
      key: 'image_set',
      status: image ? 'good' : 'bad',
      label: image ? 'Product image is set' : 'No product image — listings without images rank lower',
      hint: 'High-quality images improve CTR and SEO.',
      weight: 10,
    });

    // 9. Compare-at price (optional booster)
    if (product?.old_price && product.old_price > product.price) {
      results.push({
        key: 'sale_price',
        status: 'good',
        label: 'Has a compare-at price (looks like a sale)',
        hint: 'Sale badges boost click-through in Google Shopping.',
        weight: 5,
      });
    }

    return results;
  }, [product, focusKeyword]);

  // Score (0-100). Each check contributes its weight: good=full, ok=half, bad=0.
  const score = useMemo(() => {
    const max = checks.reduce((s, c) => s + (c.weight || 0), 0) || 1;
    const got = checks.reduce((s, c) => s + (c.status === 'good' ? c.weight : c.status === 'ok' ? c.weight / 2 : 0), 0);
    return Math.round((got / max) * 100);
  }, [checks]);

  const verdict = score >= 80 ? { label: 'Excellent', color: 'text-emerald-400', dot: 'bg-emerald-500' }
                 : score >= 60 ? { label: 'Good', color: 'text-emerald-400', dot: 'bg-emerald-500' }
                 : score >= 40 ? { label: 'Needs work', color: 'text-amber-400', dot: 'bg-amber-500' }
                 : { label: 'Poor', color: 'text-rose-400', dot: 'bg-rose-500' };

  return (
    <div className="border border-zinc-900 rounded p-4 bg-zinc-950" data-testid="seo-yoast-panel">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Search className="h-4 w-4 text-[#d4af37]" />
          <h4 className="text-zinc-100 font-medium text-sm">SEO Analysis</h4>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className={`inline-block w-2 h-2 rounded-full ${verdict.dot}`} />
          <span className={`uppercase tracking-wider ${verdict.color}`}>{verdict.label}</span>
          <span className="text-zinc-500">·</span>
          <span data-testid="seo-score" className="text-zinc-200 font-mono">{score}/100</span>
        </div>
      </div>

      <input
        type="text"
        value={focusKeyword || ''}
        onChange={(e) => onFocusKeywordChange && onFocusKeywordChange(e.target.value)}
        placeholder="Focus keyword (e.g. 'banana ice vape')"
        data-testid="seo-focus-keyword"
        className="w-full h-9 bg-transparent border border-zinc-800 rounded px-3 text-sm text-zinc-100 placeholder:text-zinc-600 mb-3 focus:outline-none focus:border-[#d4af37]"
      />

      {/* progress bar */}
      <div className="h-1.5 w-full rounded-full bg-zinc-900 overflow-hidden mb-4">
        <div className={`h-full transition-all ${verdict.dot}`} style={{ width: `${score}%` }} />
      </div>

      <div className="space-y-1.5">
        {checks.map((c) => <Check key={c.key} status={c.status} label={c.label} hint={c.hint} />)}
      </div>
    </div>
  );
};

export default SeoYoast;
